import React from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaLinkedin, FaTwitter, FaInstagram, FaEnvelope, FaArrowUp } from 'react-icons/fa';
import { profile, navItems, mailto } from '../data/profile';

const socials = [
  { name: 'GitHub', icon: FaGithub, url: profile.social.github },
  { name: 'LinkedIn', icon: FaLinkedin, url: profile.social.linkedin },
  { name: 'X / Twitter', icon: FaTwitter, url: profile.social.twitter },
  { name: 'Instagram', icon: FaInstagram, url: profile.social.instagram },
  { name: 'Email', icon: FaEnvelope, url: mailto() }
];

export default function Footer() {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer className="relative overflow-hidden border-t border-slate-200/80 bg-white py-14 dark:border-white/[0.06] dark:bg-slate-950">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-grid opacity-40" />

      <div className="relative z-10 mx-auto max-w-7xl px-6">
        <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
          <div className="max-w-sm">
            <p className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">
              {profile.name}
              <span className="text-blue-500">.</span>
            </p>
            <p className="mt-2.5 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
              {profile.role} based in {profile.location}. Currently{' '}
              {profile.availability.toLowerCase()}.
            </p>

            <ul className="mt-6 flex flex-wrap gap-2">
              {socials.map(({ name, icon: Icon, url }) => (
                <li key={name}>
                  <motion.a
                    href={url}
                    {...(url.startsWith('mailto:')
                      ? {}
                      : { target: '_blank', rel: 'noopener noreferrer' })}
                    aria-label={name}
                    whileHover={{ y: -3 }}
                    transition={{ type: 'spring', stiffness: 320, damping: 20 }}
                    className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200
                               text-slate-500 transition-colors hover:border-blue-500/40 hover:text-blue-600
                               dark:border-white/[0.08] dark:text-slate-400 dark:hover:text-blue-400"
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </motion.a>
                </li>
              ))}
            </ul>
          </div>

          <nav aria-label="Footer">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              Explore
            </h2>
            <ul className="mt-4 grid grid-cols-2 gap-x-10 gap-y-2.5 sm:grid-cols-3 md:grid-cols-2">
              {[...navItems, { name: 'Contact', href: '#contact' }].map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    className="text-sm text-slate-600 transition-colors hover:text-blue-600
                               dark:text-slate-400 dark:hover:text-blue-400"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-slate-200/80 pt-7 sm:flex-row dark:border-white/[0.06]">
          <p className="text-xs text-slate-500">
            &copy; 2019 to {new Date().getFullYear()} {profile.name}. Built and maintained by me.
          </p>

          <button
            type="button"
            onClick={scrollToTop}
            className="group inline-flex items-center gap-2 text-xs font-medium text-slate-500
                       transition-colors hover:text-blue-600 dark:hover:text-blue-400"
          >
            Back to top
            <span className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 transition-transform duration-300 group-hover:-translate-y-0.5 dark:border-white/[0.08]">
              <FaArrowUp className="h-2.5 w-2.5" aria-hidden="true" />
            </span>
          </button>
        </div>
      </div>
    </footer>
  );
}
